"use client";

// src/runtime/NavigationContext.tsx
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
var NavigationContext = createContext(null);
function NavigationProvider({ adapter, children }) {
  return /* @__PURE__ */ jsx(NavigationContext.Provider, { value: adapter, children });
}
function useNav() {
  const adapter = useContext(NavigationContext);
  if (!adapter) {
    throw new Error(
      "@audra/marketing-ui: NavigationProvider not found. Wrap your app's root with <NavigationProvider adapter={...}> exporting a NavigationAdapter that supplies Link / Image / usePathname / useNavigate / useSearchParams."
    );
  }
  return adapter;
}

// src/runtime/ScrollAware.ts
import { useEffect, useState } from "react";
function useScrolledPast(threshold = 24) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

// src/runtime/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/chrome/Navbar.tsx
import { useState as useState2 } from "react";
import { Menu, X } from "lucide-react";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var DEFAULT_LIGHT_LOGO = "/windmill-logo.svg";
var DEFAULT_DARK_LOGO = "/windmill-logo-dark.svg";
function Navbar({
  appId: _appId,
  // currently unused but reserved for per-app tweaks / analytics
  subLabel,
  logoLight = DEFAULT_LIGHT_LOGO,
  logoDark = DEFAULT_DARK_LOGO,
  navLinks,
  primaryCTA,
  secondaryCTA,
  scrollAware = "transparent-on-home",
  isHome: isHomePredicate = (p) => p === "/",
  className
}) {
  const { Link, Image, usePathname } = useNav();
  const pathname = usePathname();
  const scrolled = useScrolledPast(24);
  const [mobileOpen, setMobileOpen] = useState2(false);
  const isHome = isHomePredicate(pathname);
  const alwaysDark = scrollAware === "always-dark";
  const overHero = !alwaysDark && isHome && !scrolled;
  const useLightLogo = alwaysDark || overHero;
  const PrimaryCTAEl = primaryCTA.external ? "a" : Link;
  return /* @__PURE__ */ jsxs(
    "header",
    {
      className: cn(
        "z-50 border-b transition-all duration-300",
        isHome && !alwaysDark ? "fixed left-0 right-0 top-0" : "sticky top-0",
        alwaysDark ? "border-white/10 bg-wm-midnight/85 text-white backdrop-blur" : overHero ? "border-transparent bg-transparent text-white" : "border-gray-100 bg-white/85 text-gray-900 backdrop-blur-lg",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "mx-auto flex max-w-7xl items-center justify-between px-6 py-2.5", children: [
          /* @__PURE__ */ jsxs(Link, { href: "/", className: "flex items-center gap-2.5", "aria-label": "Windmill home", children: [
            /* @__PURE__ */ jsx2(
              Image,
              {
                src: useLightLogo ? logoLight : logoDark,
                alt: "Windmill",
                width: 144,
                height: 32,
                priority: true,
                className: "h-[26px] w-auto"
              }
            ),
            /* @__PURE__ */ jsx2("span", { className: "hidden text-xs font-medium uppercase tracking-wider opacity-70 sm:inline", children: subLabel })
          ] }),
          /* @__PURE__ */ jsx2("nav", { className: "hidden items-center gap-1 lg:flex", children: navLinks.map((link) => {
            const active = pathname === link.href || pathname.startsWith(link.href + "/");
            return /* @__PURE__ */ jsx2(
              Link,
              {
                href: link.href,
                className: cn(
                  "rounded-lg px-3.5 py-2 text-sm font-medium transition-colors",
                  active && !overHero && !alwaysDark ? "bg-gray-100 text-gray-900" : overHero || alwaysDark ? "text-gray-200 hover:bg-white/10 hover:text-white" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                ),
                children: link.label
              },
              link.href
            );
          }) }),
          /* @__PURE__ */ jsxs("div", { className: "hidden items-center gap-3 lg:flex", children: [
            secondaryCTA && (secondaryCTA.external ? /* @__PURE__ */ jsx2(
              "a",
              {
                href: secondaryCTA.href,
                className: cn(
                  "rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
                  overHero || alwaysDark ? "border-white/30 text-white hover:border-white/45 hover:bg-white/10" : "border-gray-200 text-gray-700 hover:border-brand-200 hover:bg-brand-50 hover:text-brand-600"
                ),
                children: secondaryCTA.label
              }
            ) : /* @__PURE__ */ jsx2(
              Link,
              {
                href: secondaryCTA.href,
                className: cn(
                  "rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
                  overHero || alwaysDark ? "border-white/30 text-white hover:border-white/45 hover:bg-white/10" : "border-gray-200 text-gray-700 hover:border-brand-200 hover:bg-brand-50 hover:text-brand-600"
                ),
                children: secondaryCTA.label
              }
            )),
            primaryCTA.external ? /* @__PURE__ */ jsx2(
              "a",
              {
                href: primaryCTA.href,
                className: "rounded-full bg-wm-purple-bright px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-wm-purple/30 transition-all hover:scale-[1.02] hover:brightness-110 hover:shadow-md hover:shadow-brand-600/30",
                children: primaryCTA.label
              }
            ) : /* @__PURE__ */ jsx2(
              Link,
              {
                href: primaryCTA.href,
                className: "rounded-full bg-wm-purple-bright px-5 py-2 text-sm font-semibold text-white shadow-sm shadow-wm-purple/30 transition-all hover:scale-[1.02] hover:brightness-110 hover:shadow-md hover:shadow-brand-600/30",
                children: primaryCTA.label
              }
            )
          ] }),
          /* @__PURE__ */ jsx2(
            "button",
            {
              className: cn(
                "rounded-lg p-2 lg:hidden",
                overHero || alwaysDark ? "text-white hover:bg-white/10" : "text-gray-600 hover:bg-gray-100"
              ),
              onClick: () => setMobileOpen((v) => !v),
              "aria-label": mobileOpen ? "Close menu" : "Open menu",
              children: mobileOpen ? /* @__PURE__ */ jsx2(X, { className: "h-5 w-5" }) : /* @__PURE__ */ jsx2(Menu, { className: "h-5 w-5" })
            }
          )
        ] }),
        mobileOpen && /* @__PURE__ */ jsxs("nav", { className: "border-t border-white/10 bg-navy px-6 py-4 lg:hidden", children: [
          /* @__PURE__ */ jsx2("div", { className: "space-y-1", children: navLinks.map((link) => {
            const active = pathname === link.href || pathname.startsWith(link.href + "/");
            return /* @__PURE__ */ jsx2(
              Link,
              {
                href: link.href,
                onClick: () => setMobileOpen(false),
                className: cn(
                  "block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  active ? "bg-white/10 text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"
                ),
                children: link.label
              },
              link.href
            );
          }) }),
          /* @__PURE__ */ jsxs("div", { className: "mt-4 flex flex-col gap-2 border-t border-white/10 pt-4", children: [
            secondaryCTA && (secondaryCTA.external ? /* @__PURE__ */ jsx2(
              "a",
              {
                href: secondaryCTA.href,
                onClick: () => setMobileOpen(false),
                className: "rounded-full border border-white/25 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-white/10",
                children: secondaryCTA.label
              }
            ) : /* @__PURE__ */ jsx2(
              Link,
              {
                href: secondaryCTA.href,
                onClick: () => setMobileOpen(false),
                className: "rounded-full border border-white/25 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-white/10",
                children: secondaryCTA.label
              }
            )),
            primaryCTA.external ? /* @__PURE__ */ jsx2(
              "a",
              {
                href: primaryCTA.href,
                onClick: () => setMobileOpen(false),
                className: "rounded-full bg-wm-purple-bright px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:brightness-110",
                children: primaryCTA.label
              }
            ) : /* @__PURE__ */ jsx2(
              Link,
              {
                href: primaryCTA.href,
                onClick: () => setMobileOpen(false),
                className: "rounded-full bg-wm-purple-bright px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:brightness-110",
                children: primaryCTA.label
              }
            )
          ] })
        ] })
      ]
    }
  );
}

// src/chrome/Footer.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var DEFAULT_FOOTER_LOGO = "/windmill-footer-logo.svg";
var DEFAULT_LEGAL_LINKS = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms & Conditions", href: "/terms" }
];
function Footer({
  appId: _appId,
  subLabel,
  description,
  footerLogo = DEFAULT_FOOTER_LOGO,
  columns,
  social,
  copyrightEntity = "Windmill Smart Solutions AG",
  legalLinks = DEFAULT_LEGAL_LINKS,
  className
}) {
  const { Link, Image } = useNav();
  const year = (/* @__PURE__ */ new Date()).getFullYear();
  return /* @__PURE__ */ jsx3("footer", { className: cn("border-t border-white/10 bg-wm-midnight", className), children: /* @__PURE__ */ jsxs2("div", { className: "mx-auto max-w-7xl px-6 py-16", children: [
    /* @__PURE__ */ jsxs2("div", { className: "grid gap-12 lg:grid-cols-5", children: [
      /* @__PURE__ */ jsxs2("div", { className: "lg:col-span-2", children: [
        /* @__PURE__ */ jsxs2(
          Link,
          {
            href: "/",
            className: "inline-flex items-center gap-2.5",
            "aria-label": "Windmill home",
            children: [
              /* @__PURE__ */ jsx3(
                Image,
                {
                  src: footerLogo,
                  alt: "Windmill",
                  width: 162,
                  height: 36,
                  className: "h-7 w-auto"
                }
              ),
              /* @__PURE__ */ jsx3("span", { className: "text-xs font-medium uppercase tracking-wider text-wm-gray-100/50", children: subLabel })
            ]
          }
        ),
        /* @__PURE__ */ jsx3("p", { className: "mt-4 text-sm leading-relaxed text-wm-gray-100/60", children: description }),
        social && social.length > 0 && /* @__PURE__ */ jsx3("div", { className: "mt-6 flex items-center gap-4", children: social.map((s) => /* @__PURE__ */ jsx3(
          "a",
          {
            href: s.href,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "text-gray-500 transition-colors hover:text-white",
            "aria-label": s.label,
            children: /* @__PURE__ */ jsx3(SocialIcon, { icon: s.icon })
          },
          s.href
        )) })
      ] }),
      columns.map((column) => /* @__PURE__ */ jsxs2("div", { children: [
        /* @__PURE__ */ jsx3("p", { className: "text-sm font-semibold uppercase tracking-wider text-gray-400", children: column.title }),
        /* @__PURE__ */ jsx3("ul", { className: "mt-4 space-y-3", children: column.links.map((link) => /* @__PURE__ */ jsx3("li", { children: link.external ? /* @__PURE__ */ jsx3(
          "a",
          {
            href: link.href,
            target: link.href.startsWith("http") ? "_blank" : void 0,
            rel: link.href.startsWith("http") ? "noopener noreferrer" : void 0,
            className: "text-sm text-gray-500 transition-colors hover:text-white",
            children: link.label
          }
        ) : /* @__PURE__ */ jsx3(
          Link,
          {
            href: link.href,
            className: "text-sm text-gray-500 transition-colors hover:text-white",
            children: link.label
          }
        ) }, `${column.title}-${link.label}`)) })
      ] }, column.title))
    ] }),
    /* @__PURE__ */ jsx3("div", { className: "mt-12 border-t border-navy-border" }),
    /* @__PURE__ */ jsxs2("div", { className: "mt-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center", children: [
      /* @__PURE__ */ jsxs2("p", { className: "text-xs text-gray-600", children: [
        "\xA9 ",
        year,
        " ",
        copyrightEntity,
        ". All rights reserved."
      ] }),
      /* @__PURE__ */ jsx3("div", { className: "flex items-center gap-4 text-xs text-gray-600", children: legalLinks.map((legal, i) => /* @__PURE__ */ jsxs2("span", { className: "flex items-center gap-4", children: [
        i > 0 && /* @__PURE__ */ jsx3("span", { "aria-hidden": true, children: "\xB7" }),
        legal.href.startsWith("http") ? /* @__PURE__ */ jsx3(
          "a",
          {
            href: legal.href,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "transition-colors hover:text-gray-400",
            children: legal.label
          }
        ) : /* @__PURE__ */ jsx3(
          Link,
          {
            href: legal.href,
            className: "transition-colors hover:text-gray-400",
            children: legal.label
          }
        )
      ] }, legal.href)) })
    ] })
  ] }) });
}
function SocialIcon({ icon }) {
  if (icon === "linkedin") {
    return /* @__PURE__ */ jsx3("svg", { className: "h-5 w-5", fill: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx3("path", { d: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" }) });
  }
  return /* @__PURE__ */ jsx3("svg", { className: "h-5 w-5", fill: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx3("path", { d: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" }) });
}

// src/chrome/AppSwitcher.tsx
import { useEffect as useEffect3, useRef, useState as useState4 } from "react";

// src/auth/SessionProvider.tsx
import { createContext as createContext2, useContext as useContext2, useEffect as useEffect2, useState as useState3 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var SessionContext = createContext2(null);
function SessionProvider({
  audraIdOrigin,
  on401 = "redirect",
  children
}) {
  const [session, setSession] = useState3(null);
  const [isLoading, setIsLoading] = useState3(true);
  const [isReady, setIsReady] = useState3(false);
  const meUrl = `${audraIdOrigin.replace(/\/$/, "")}/api/me`;
  async function fetchMe() {
    const resp = await fetch(meUrl, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" }
    });
    if (resp.status === 401) return null;
    if (!resp.ok) throw new Error(`audra-id /api/me ${resp.status}`);
    return await resp.json();
  }
  async function refresh() {
    setIsLoading(true);
    try {
      const next = await fetchMe();
      setSession(next);
      if (next === null && on401 === "redirect") {
      }
    } finally {
      setIsLoading(false);
      setIsReady(true);
    }
  }
  useEffect2(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await fetchMe();
        if (!mounted) return;
        if (me === null && on401 === "redirect") {
          window.location.href = buildLoginUrl(audraIdOrigin);
          return;
        }
        setSession(me);
      } catch {
      } finally {
        if (mounted) {
          setIsLoading(false);
          setIsReady(true);
        }
      }
    })();
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      mounted = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [audraIdOrigin, on401]);
  const value = {
    session,
    isLoading,
    isReady,
    rolesFor: (appSlug) => session?.app_roles?.[appSlug] ?? [],
    refresh,
    loginUrl: (returnTo) => buildLoginUrl(audraIdOrigin, returnTo),
    signOut: async () => {
      const resp = await fetch(`${audraIdOrigin}/api/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      if (resp.ok) {
        const body = await resp.json();
        window.location.href = body.entra_logout_url ?? buildLoginUrl(audraIdOrigin);
      } else {
        window.location.href = buildLoginUrl(audraIdOrigin);
      }
    }
  };
  return /* @__PURE__ */ jsx4(SessionContext.Provider, { value, children });
}
function buildLoginUrl(audraIdOrigin, returnTo) {
  const r = returnTo ?? `${window.location.pathname}${window.location.search}`;
  return `${audraIdOrigin.replace(/\/$/, "")}/auth/entra/login?return_to=${encodeURIComponent(
    `${window.location.origin}${r}`
  )}`;
}
function useSession() {
  const ctx = useContext2(SessionContext);
  if (!ctx) {
    throw new Error(
      "useSession must be used inside <SessionProvider>. Wrap your authed root."
    );
  }
  return ctx;
}

// src/chrome/AppSwitcher.tsx
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
function AppSwitcher({
  audraIdOrigin,
  currentAppSlug,
  className
}) {
  const { isReady, session } = useSession();
  const [open, setOpen] = useState4(false);
  const [apps, setApps] = useState4(null);
  const [loadError, setLoadError] = useState4(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  useEffect3(() => {
    if (!open || apps !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetch(`${audraIdOrigin.replace(/\/$/, "")}/api/apps`, {
          credentials: "include",
          headers: { Accept: "application/json" }
        });
        if (!resp.ok) throw new Error(String(resp.status));
        const body = await resp.json();
        if (!cancelled) setApps(body.apps);
      } catch {
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, apps, audraIdOrigin]);
  useEffect3(() => {
    if (!open) return;
    function onClick(e) {
      const t = e.target;
      if (panelRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  if (!isReady || !session) return null;
  return /* @__PURE__ */ jsxs3("div", { className: "relative inline-block", children: [
    /* @__PURE__ */ jsx5(
      "button",
      {
        ref: buttonRef,
        type: "button",
        "aria-label": "Open app switcher",
        "aria-haspopup": "true",
        "aria-expanded": open,
        onClick: () => setOpen((v) => !v),
        className: className ?? "grid h-9 w-9 place-items-center rounded-md border border-white/10 text-wm-gray-100/80 transition-colors hover:bg-white/5 hover:text-wm-white",
        children: /* @__PURE__ */ jsx5(NineDotsIcon, {})
      }
    ),
    open && /* @__PURE__ */ jsx5(
      "div",
      {
        ref: panelRef,
        role: "menu",
        className: "absolute right-0 top-full z-50 mt-2 w-[340px] rounded-xl border border-white/10 bg-wm-black/95 p-3 shadow-2xl backdrop-blur",
        children: loadError ? /* @__PURE__ */ jsxs3("p", { className: "px-3 py-6 text-center text-sm text-red-300", children: [
          "Couldn't load apps.",
          " ",
          /* @__PURE__ */ jsx5(
            "button",
            {
              className: "underline",
              onClick: () => {
                setApps(null);
                setLoadError(false);
              },
              children: "Retry"
            }
          )
        ] }) : apps === null ? /* @__PURE__ */ jsx5("p", { className: "px-3 py-6 text-center text-sm text-wm-gray-100/60", children: "Loading\u2026" }) : apps.length === 0 ? /* @__PURE__ */ jsx5("p", { className: "px-3 py-6 text-center text-sm text-wm-gray-100/60", children: "No apps available for your account yet. Ask an audra-id admin to grant access." }) : /* @__PURE__ */ jsx5("ul", { className: "grid grid-cols-3 gap-2", children: apps.map((app) => {
          const isCurrent = app.slug === currentAppSlug;
          return /* @__PURE__ */ jsx5("li", { className: "contents", children: /* @__PURE__ */ jsxs3(
            "a",
            {
              href: app.url,
              "aria-current": isCurrent ? "page" : void 0,
              className: "flex flex-col items-center gap-1 rounded-lg p-3 text-center text-xs transition-colors " + (isCurrent ? "bg-wm-purple-bright/15 text-wm-purple-bright" : "text-wm-gray-100/80 hover:bg-white/5 hover:text-wm-white"),
              title: app.description ?? app.name,
              children: [
                /* @__PURE__ */ jsx5(AppIcon, { icon: app.icon_url, fallback: app.name }),
                /* @__PURE__ */ jsx5("span", { className: "font-medium", children: app.name })
              ]
            }
          ) }, app.id);
        }) })
      }
    )
  ] });
}
function NineDotsIcon() {
  return /* @__PURE__ */ jsx5("svg", { viewBox: "0 0 24 24", className: "h-5 w-5", fill: "currentColor", "aria-hidden": true, children: [4, 12, 20].flatMap(
    (y) => [4, 12, 20].map((x) => /* @__PURE__ */ jsx5("circle", { cx: x, cy: y, r: 1.6 }, `${x}-${y}`))
  ) });
}
function AppIcon({ icon, fallback }) {
  if (icon) {
    return /* @__PURE__ */ jsx5(
      "img",
      {
        src: icon,
        alt: "",
        className: "h-9 w-9 rounded-md object-contain",
        loading: "lazy"
      }
    );
  }
  const initials = fallback.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");
  return /* @__PURE__ */ jsx5("span", { className: "grid h-9 w-9 place-items-center rounded-md bg-wm-purple-bright/15 text-sm font-semibold text-wm-purple-bright", children: initials });
}

// src/auth/LoginScreen.tsx
import { useEffect as useEffect4, useState as useState5 } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";

// src/auth/MicrosoftIcon.tsx
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function MicrosoftIcon({ size = 18 }) {
  return /* @__PURE__ */ jsxs4("svg", { width: size, height: size, viewBox: "0 0 21 21", "aria-hidden": true, focusable: false, children: [
    /* @__PURE__ */ jsx6("rect", { x: "1", y: "1", width: "9", height: "9", fill: "#F25022" }),
    /* @__PURE__ */ jsx6("rect", { x: "11", y: "1", width: "9", height: "9", fill: "#7FBA00" }),
    /* @__PURE__ */ jsx6("rect", { x: "1", y: "11", width: "9", height: "9", fill: "#00A4EF" }),
    /* @__PURE__ */ jsx6("rect", { x: "11", y: "11", width: "9", height: "9", fill: "#FFB900" })
  ] });
}

// src/auth/LoginScreen.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
function LoginScreen({
  appId,
  subLabel,
  heading = "Welcome back",
  subHeading,
  defaultReturnTo = "/dashboard",
  logo = "/windmill-logo-dark.svg",
  onSubmit,
  onMicrosoftSSO,
  onSelectOrg,
  microsoftEnabled = true,
  demoLogins,
  showDemoLogins = false,
  onForgotPassword
}) {
  const { Image, useNavigate, useSearchParams } = useNav();
  const navigate = useNavigate();
  const searchParams = useSearchParams();
  const REMEMBER_ME_KEY = `${appId}-remember-me`;
  const RETURN_TO_KEY = `${appId}-sso-return-to`;
  const computedSubHeading = subHeading ?? `Continue to your ${subLabel} workspace.`;
  const [email, setEmail] = useState5("");
  const [password, setPassword] = useState5("");
  const [showPassword, setShowPassword] = useState5(false);
  const [rememberMe, setRememberMe] = useState5(false);
  const [error, setError] = useState5("");
  const [loading, setLoading] = useState5(false);
  const [orgChoices, setOrgChoices] = useState5(null);
  const [ssoSessionKey, setSsoSessionKey] = useState5(null);
  const consumeReturnTo = () => {
    try {
      const v = sessionStorage.getItem(RETURN_TO_KEY);
      if (v && v.startsWith("/") && !v.startsWith("/login")) {
        sessionStorage.removeItem(RETURN_TO_KEY);
        return v;
      }
    } catch {
    }
    return defaultReturnTo;
  };
  useEffect4(() => {
    const ssoError = searchParams.get("error");
    const ssoDescription = searchParams.get("error_description");
    const ssoCode = searchParams.get("sso");
    const ssoDetail = searchParams.get("detail");
    if (ssoError) {
      setError(
        ssoDescription ? `SSO login failed: ${ssoDescription}` : `SSO login failed (${ssoError}). Please try again.`
      );
      return;
    }
    if (ssoCode === "choose_org") {
      const session = searchParams.get("sso_session");
      const orgsParam = searchParams.get("orgs");
      if (session && orgsParam) {
        try {
          const orgs = JSON.parse(decodeURIComponent(orgsParam));
          setSsoSessionKey(session);
          setOrgChoices(orgs);
        } catch {
          setError("Failed to parse organization list. Please try again.");
        }
      } else {
        setError("SSO session data missing. Please try again.");
      }
      return;
    }
    if (ssoCode) {
      const messages = {
        error: ssoDetail || "SSO authentication failed.",
        missing: "Missing authorization code or state. Please try again.",
        invalid_state: "Invalid or expired SSO state. Please try again.",
        provider_not_found: "SSO provider not found. Contact your administrator.",
        discovery_failed: "Could not load SSO configuration. Contact your administrator.",
        exchange_failed: "Token exchange failed. Please try again.",
        userinfo_failed: "Could not retrieve your profile.",
        no_email: "Your identity provider did not return an email address.",
        account_disabled: "Your account is disabled. Contact your administrator.",
        no_account: ssoDetail || "You need an invitation to join this organization. Ask an admin to invite you."
      };
      setError(messages[ssoCode] || (ssoDetail ? `SSO: ${ssoDetail}` : "SSO sign-in failed."));
    }
  }, [searchParams]);
  useEffect4(() => {
    try {
      setRememberMe(localStorage.getItem(REMEMBER_ME_KEY) === "true");
    } catch {
    }
  }, [REMEMBER_ME_KEY]);
  const handleSuccess = () => {
    try {
      localStorage.setItem(REMEMBER_ME_KEY, String(rememberMe));
    } catch {
    }
    navigate(consumeReturnTo(), { replace: true });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await onSubmit({
      email,
      password,
      rememberMe
    });
    if (result.ok) {
      handleSuccess();
      return;
    }
    if ("organizations" in result) {
      setOrgChoices(result.organizations);
      setLoading(false);
      return;
    }
    setError(result.error || "Invalid email or password");
    setLoading(false);
  };
  const handleOrgSelect = async (chosenTenantId) => {
    setOrgChoices(null);
    setLoading(true);
    let result;
    if (onSelectOrg) {
      result = await onSelectOrg(chosenTenantId, ssoSessionKey ?? void 0);
      setSsoSessionKey(null);
    } else {
      const submitResult = await onSubmit({
        email,
        password,
        rememberMe,
        tenantId: chosenTenantId
      });
      result = submitResult.ok ? { ok: true } : { ok: false, error: "error" in submitResult ? submitResult.error : "Login failed. Please try again." };
    }
    if (result.ok) {
      handleSuccess();
      return;
    }
    setError(result.error || "Login failed. Please try again.");
    setLoading(false);
  };
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      className: "wm-shell-bg",
      style: {
        position: "fixed",
        inset: 0,
        overflow: "auto",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 16px"
      },
      children: [
        /* @__PURE__ */ jsx7(
          "div",
          {
            className: "pointer-events-none absolute animate-wm-float",
            style: {
              left: -64,
              top: 40,
              width: 208,
              height: 208,
              borderRadius: "50%",
              background: "rgba(124,74,247,0.30)",
              filter: "blur(48px)"
            }
          }
        ),
        /* @__PURE__ */ jsx7(
          "div",
          {
            className: "pointer-events-none absolute animate-wm-float",
            style: {
              right: -80,
              bottom: 0,
              width: 288,
              height: 288,
              borderRadius: "50%",
              background: "rgba(124,74,247,0.18)",
              filter: "blur(48px)",
              animationDelay: "3s"
            }
          }
        ),
        /* @__PURE__ */ jsxs5(
          "section",
          {
            className: "wm-panel relative z-10 w-full animate-wm-reveal",
            style: { maxWidth: 440 },
            children: [
              /* @__PURE__ */ jsxs5(
                "div",
                {
                  style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 28
                  },
                  children: [
                    /* @__PURE__ */ jsx7(
                      "button",
                      {
                        type: "button",
                        "aria-label": `${subLabel} home`,
                        onClick: () => navigate("/"),
                        style: {
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer"
                        },
                        children: /* @__PURE__ */ jsx7(Image, { src: logo, alt: subLabel, width: 140, height: 32, className: "h-8 w-auto" })
                      }
                    ),
                    /* @__PURE__ */ jsx7(
                      "span",
                      {
                        style: {
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#7C4AF7",
                          letterSpacing: "0.12em",
                          textTransform: "uppercase"
                        },
                        children: subLabel
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs5("div", { style: { marginBottom: 24 }, children: [
                /* @__PURE__ */ jsx7(
                  "h2",
                  {
                    style: {
                      fontFamily: '"ClashDisplay", Inter, sans-serif',
                      fontSize: 28,
                      fontWeight: 500,
                      color: "#FFFFFF",
                      margin: 0
                    },
                    children: heading
                  }
                ),
                /* @__PURE__ */ jsx7(
                  "p",
                  {
                    style: {
                      marginTop: 8,
                      fontSize: 14,
                      color: "rgba(244,244,244,0.7)",
                      margin: "8px 0 0"
                    },
                    children: computedSubHeading
                  }
                )
              ] }),
              orgChoices && /* @__PURE__ */ jsxs5("div", { className: "space-y-3", children: [
                /* @__PURE__ */ jsx7("p", { style: { fontSize: 14, color: "#4f4a65", textAlign: "center" }, children: "This email belongs to multiple organizations. Select one:" }),
                orgChoices.map((org) => /* @__PURE__ */ jsxs5(
                  "button",
                  {
                    type: "button",
                    onClick: () => handleOrgSelect(org.tenantId),
                    disabled: loading,
                    style: {
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "14px 18px",
                      borderRadius: 14,
                      fontSize: 14,
                      cursor: "pointer",
                      border: "1px solid #d8d1eb",
                      background: "white",
                      transition: "border-color 0.2s, background 0.2s"
                    },
                    onMouseEnter: (e) => {
                      e.currentTarget.style.borderColor = "#7c4af7";
                      e.currentTarget.style.background = "#faf9fd";
                    },
                    onMouseLeave: (e) => {
                      e.currentTarget.style.borderColor = "#d8d1eb";
                      e.currentTarget.style.background = "white";
                    },
                    children: [
                      /* @__PURE__ */ jsx7("span", { style: { fontWeight: 500, color: "#050019" }, children: org.orgName }),
                      /* @__PURE__ */ jsx7(
                        "span",
                        {
                          style: {
                            fontSize: 11,
                            color: "#9a93ae",
                            textTransform: "capitalize"
                          },
                          children: org.role
                        }
                      )
                    ]
                  },
                  org.tenantId
                )),
                /* @__PURE__ */ jsx7(
                  "button",
                  {
                    type: "button",
                    onClick: () => {
                      setOrgChoices(null);
                      setSsoSessionKey(null);
                      setError("");
                    },
                    style: {
                      width: "100%",
                      padding: 8,
                      fontSize: 12,
                      color: "#9a93ae",
                      background: "none",
                      border: "none",
                      cursor: "pointer"
                    },
                    children: "Back"
                  }
                )
              ] }),
              !orgChoices && /* @__PURE__ */ jsxs5("form", { onSubmit: handleSubmit, noValidate: true, children: [
                /* @__PURE__ */ jsxs5("div", { style: { marginBottom: 20 }, children: [
                  /* @__PURE__ */ jsx7(
                    "label",
                    {
                      htmlFor: "wm-login-email",
                      style: {
                        display: "block",
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#39354f",
                        marginBottom: 6
                      },
                      children: "Email"
                    }
                  ),
                  /* @__PURE__ */ jsx7(
                    "input",
                    {
                      id: "wm-login-email",
                      type: "email",
                      value: email,
                      onChange: (e) => {
                        setEmail(e.target.value);
                        setError("");
                      },
                      placeholder: "you@company.com",
                      autoComplete: "email",
                      autoFocus: true,
                      style: {
                        width: "100%",
                        height: 46,
                        borderRadius: 12,
                        border: "1.5px solid #d8d1eb",
                        padding: "0 16px",
                        fontSize: 14,
                        outline: "none",
                        color: "#050019",
                        backgroundColor: "white",
                        transition: "border-color 0.2s, box-shadow 0.2s",
                        boxSizing: "border-box"
                      },
                      onFocus: (e) => {
                        e.currentTarget.style.borderColor = "#7c4af7";
                        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(124,74,247,0.1)";
                      },
                      onBlur: (e) => {
                        e.currentTarget.style.borderColor = "#d8d1eb";
                        e.currentTarget.style.boxShadow = "none";
                      }
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs5("div", { style: { marginBottom: 16 }, children: [
                  /* @__PURE__ */ jsxs5(
                    "div",
                    {
                      style: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 6
                      },
                      children: [
                        /* @__PURE__ */ jsx7(
                          "label",
                          {
                            htmlFor: "wm-login-password",
                            style: { fontSize: 13, fontWeight: 500, color: "#39354f" },
                            children: "Password"
                          }
                        ),
                        onForgotPassword && /* @__PURE__ */ jsx7(
                          "button",
                          {
                            type: "button",
                            onClick: onForgotPassword,
                            style: {
                              fontSize: 13,
                              color: "#7c4af7",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              fontWeight: 500
                            },
                            children: "Forgot password?"
                          }
                        )
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs5("div", { style: { position: "relative" }, children: [
                    /* @__PURE__ */ jsx7(
                      "input",
                      {
                        id: "wm-login-password",
                        type: showPassword ? "text" : "password",
                        value: password,
                        onChange: (e) => {
                          setPassword(e.target.value);
                          setError("");
                        },
                        placeholder: "Enter your password",
                        autoComplete: "current-password",
                        style: {
                          width: "100%",
                          height: 46,
                          borderRadius: 12,
                          border: "1.5px solid #d8d1eb",
                          padding: "0 42px 0 16px",
                          fontSize: 14,
                          outline: "none",
                          color: "#050019",
                          backgroundColor: "white",
                          transition: "border-color 0.2s, box-shadow 0.2s",
                          boxSizing: "border-box"
                        },
                        onFocus: (e) => {
                          e.currentTarget.style.borderColor = "#7c4af7";
                          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(124,74,247,0.1)";
                        },
                        onBlur: (e) => {
                          e.currentTarget.style.borderColor = "#d8d1eb";
                          e.currentTarget.style.boxShadow = "none";
                        }
                      }
                    ),
                    /* @__PURE__ */ jsx7(
                      "button",
                      {
                        type: "button",
                        tabIndex: -1,
                        disabled: password.length === 0,
                        onClick: () => setShowPassword((v) => !v),
                        "aria-label": showPassword ? "Hide password" : "Show password",
                        style: {
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          padding: 4,
                          display: "flex",
                          alignItems: "center",
                          cursor: password.length > 0 ? "pointer" : "default",
                          color: password.length > 0 ? "#6d6785" : "#ccc6df",
                          transition: "color 0.2s"
                        },
                        children: showPassword ? /* @__PURE__ */ jsx7(EyeOff, { size: 18 }) : /* @__PURE__ */ jsx7(Eye, { size: 18 })
                      }
                    )
                  ] })
                ] }),
                /* @__PURE__ */ jsx7("div", { style: { display: "flex", alignItems: "center", marginBottom: 20 }, children: /* @__PURE__ */ jsxs5(
                  "label",
                  {
                    style: {
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      userSelect: "none",
                      fontSize: 13,
                      color: "#4f4a65"
                    },
                    children: [
                      /* @__PURE__ */ jsx7(
                        "input",
                        {
                          type: "checkbox",
                          checked: rememberMe,
                          onChange: (e) => setRememberMe(e.target.checked),
                          style: {
                            width: 16,
                            height: 16,
                            margin: 0,
                            accentColor: "#7c4af7",
                            cursor: "pointer"
                          }
                        }
                      ),
                      "Remember me"
                    ]
                  }
                ) }),
                error && /* @__PURE__ */ jsx7(
                  "div",
                  {
                    role: "alert",
                    style: {
                      fontSize: 13,
                      padding: "10px 14px",
                      borderRadius: 10,
                      marginBottom: 16,
                      background: "rgba(231,0,11,0.06)",
                      color: "#c10007",
                      border: "1px solid rgba(231,0,11,0.15)"
                    },
                    children: error
                  }
                ),
                showDemoLogins && demoLogins && demoLogins.length > 0 && /* @__PURE__ */ jsxs5(
                  "div",
                  {
                    style: {
                      borderRadius: 12,
                      border: "1px solid #d9c8ff",
                      padding: "12px 14px",
                      marginBottom: 20,
                      backgroundColor: "rgba(245,242,255,0.8)"
                    },
                    children: [
                      /* @__PURE__ */ jsx7(
                        "p",
                        {
                          style: {
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#512ab2",
                            margin: "0 0 8px"
                          },
                          children: "Demo Logins"
                        }
                      ),
                      /* @__PURE__ */ jsx7("div", { style: { display: "flex", gap: 8, flexWrap: "wrap" }, children: demoLogins.map((cred) => /* @__PURE__ */ jsxs5(
                        "button",
                        {
                          type: "button",
                          onClick: () => {
                            setEmail(cred.email);
                            setPassword(cred.password);
                            setError("");
                          },
                          style: {
                            flex: "1 1 0",
                            minWidth: 0,
                            padding: "8px 10px",
                            borderRadius: 8,
                            cursor: "pointer",
                            border: "1px solid #d9c8ff",
                            background: "white",
                            textAlign: "left",
                            transition: "border-color 0.15s, background 0.15s"
                          },
                          onMouseEnter: (e) => {
                            e.currentTarget.style.borderColor = "#7c4af7";
                            e.currentTarget.style.background = "#faf9fd";
                          },
                          onMouseLeave: (e) => {
                            e.currentTarget.style.borderColor = "#d9c8ff";
                            e.currentTarget.style.background = "white";
                          },
                          children: [
                            /* @__PURE__ */ jsx7(
                              "span",
                              {
                                style: {
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#7c4af7",
                                  display: "block"
                                },
                                children: cred.label
                              }
                            ),
                            /* @__PURE__ */ jsx7(
                              "span",
                              {
                                style: {
                                  fontSize: 11,
                                  color: "#6d6785",
                                  display: "block",
                                  marginTop: 2
                                },
                                children: cred.email
                              }
                            )
                          ]
                        },
                        cred.email
                      )) })
                    ]
                  }
                ),
                /* @__PURE__ */ jsx7(
                  "button",
                  {
                    type: "submit",
                    disabled: loading,
                    style: {
                      width: "100%",
                      height: 48,
                      borderRadius: 14,
                      border: "none",
                      fontSize: 15,
                      fontWeight: 600,
                      color: "white",
                      cursor: loading ? "default" : "pointer",
                      background: "linear-gradient(135deg, #7c4af7 0%, #6734e4 100%)",
                      boxShadow: "0 6px 20px rgba(124,74,247,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      transition: "transform 0.15s, box-shadow 0.15s",
                      opacity: loading ? 0.6 : 1
                    },
                    onMouseEnter: (e) => {
                      if (loading) return;
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 8px 28px rgba(124,74,247,0.4), inset 0 1px 0 rgba(255,255,255,0.15)";
                    },
                    onMouseLeave: (e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 6px 20px rgba(124,74,247,0.3), inset 0 1px 0 rgba(255,255,255,0.15)";
                    },
                    children: loading ? /* @__PURE__ */ jsx7(Loader2, { className: "w-5 h-5 animate-spin" }) : "Sign in"
                  }
                ),
                microsoftEnabled && /* @__PURE__ */ jsxs5(Fragment, { children: [
                  /* @__PURE__ */ jsxs5("div", { style: { position: "relative", margin: "22px 0" }, children: [
                    /* @__PURE__ */ jsx7(
                      "div",
                      {
                        style: {
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center"
                        },
                        children: /* @__PURE__ */ jsx7(
                          "span",
                          {
                            style: { width: "100%", borderTop: "1px solid #d8d1eb" }
                          }
                        )
                      }
                    ),
                    /* @__PURE__ */ jsx7(
                      "div",
                      {
                        style: {
                          position: "relative",
                          display: "flex",
                          justifyContent: "center"
                        },
                        children: /* @__PURE__ */ jsx7(
                          "span",
                          {
                            style: {
                              padding: "0 10px",
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              backgroundColor: "#f5f2ff",
                              color: "#9a93ae"
                            },
                            children: "Or continue with"
                          }
                        )
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsxs5(
                    "button",
                    {
                      type: "button",
                      onClick: () => void onMicrosoftSSO(),
                      disabled: loading,
                      style: {
                        width: "100%",
                        height: 48,
                        borderRadius: 14,
                        border: "1.5px solid #d8d1eb",
                        fontSize: 14,
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 10,
                        cursor: loading ? "default" : "pointer",
                        backgroundColor: "white",
                        color: "#39354f",
                        opacity: loading ? 0.6 : 1,
                        transition: "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
                        boxShadow: "0 2px 8px rgba(5,0,25,0.06)"
                      },
                      onMouseEnter: (e) => {
                        if (loading) return;
                        e.currentTarget.style.borderColor = "#7c4af7";
                        e.currentTarget.style.backgroundColor = "#faf9fd";
                        e.currentTarget.style.boxShadow = "0 4px 14px rgba(5,0,25,0.1)";
                      },
                      onMouseLeave: (e) => {
                        e.currentTarget.style.borderColor = "#d8d1eb";
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.boxShadow = "0 2px 8px rgba(5,0,25,0.06)";
                      },
                      children: [
                        /* @__PURE__ */ jsx7(MicrosoftIcon, {}),
                        "Sign in with Microsoft"
                      ]
                    }
                  )
                ] })
              ] }),
              /* @__PURE__ */ jsxs5(
                "p",
                {
                  style: {
                    marginTop: 20,
                    textAlign: "center",
                    fontSize: 12,
                    color: "rgba(244,244,244,0.5)"
                  },
                  children: [
                    "\xA9 ",
                    (/* @__PURE__ */ new Date()).getFullYear(),
                    " Windmill Smart Solutions AG \xB7",
                    " ",
                    /* @__PURE__ */ jsx7(
                      "a",
                      {
                        href: "https://windmill.digital",
                        style: { color: "inherit", textDecoration: "none" },
                        children: "windmill.digital"
                      }
                    )
                  ]
                }
              )
            ]
          }
        )
      ]
    }
  );
}
export {
  AppSwitcher,
  Footer,
  LoginScreen,
  MicrosoftIcon,
  Navbar,
  NavigationProvider,
  SessionProvider,
  cn,
  useNav,
  useScrolledPast,
  useSession
};
//# sourceMappingURL=index.js.map